/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import axios from 'axios';
import { buildApiUrl } from '../config/api';

/**
 * Custom React Query hook for fetching intern dashboard data
 * This replaces the manual fetchDashboardData function with caching and optimization
 */

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Transform internships data from snake_case to camelCase
const transformInternships = (jobs) => {
  return (jobs || []).map(job => ({
    ...job,
    jobTitle: job.job_title || job.jobTitle || job.title,
    companyName: job.company_name || job.companyName || job.company,
    company_name: job.company_name || job.companyName || job.company,
    salary: job.salary_range || job.salary || job.stipend_range || job.stipend,
    remoteOption: job.remote_option || job.remoteOption,
    jobType: job.job_type || job.jobType || job.type,
    experienceRequired: job.experience_required || job.experienceRequired,
    requiredSkills: job.required_skills || job.requiredSkills,
    educationRequired: job.education_required || job.educationRequired,
    applicationDeadline: job.application_deadline || job.applicationDeadline,
    companyWebsite: job.company_website || job.companyWebsite,
    location: job.location || job.job_location || job.city
  }));
};

// Transform saved internships
const transformSavedInternships = (savedInternships) => {
  return (savedInternships || []).map(internship => ({
    ...internship,
    _id: internship._id || internship.id,
    jobTitle: internship.title || internship.job_title || internship.jobTitle,
    companyName: internship.company || internship.company_name || internship.companyName,
    salary: internship.stipend || internship.salary_range || internship.salary,
    remoteOption: internship.remote_option || internship.remoteOption,
    jobType: internship.type || internship.job_type || internship.jobType,
    experienceRequired: internship.experience_required || internship.experienceRequired,
    requiredSkills: internship.required_skills || internship.requiredSkills || [],
    educationRequired: internship.education_required || internship.educationRequired,
    applicationDeadline: internship.application_deadline || internship.applicationDeadline,
    companyWebsite: internship.company_website || internship.companyWebsite,
    location: internship.location || internship.job_location,
    description: internship.description || internship.job_description
  }));
};

// Sort internships by preferred role
const sortByPreferredRole = (internships, preferredRole) => {
  if (!preferredRole || !preferredRole.trim()) {
    return internships;
  }

  return [...internships].sort((a, b) => {
    const aTitle = (a.jobTitle || a.job_title || a.title || '').toLowerCase();
    const bTitle = (b.jobTitle || b.job_title || b.title || '').toLowerCase();
    const preferredLower = preferredRole.toLowerCase();
    
    const keywords = preferredLower.split(/[\s,]+/).filter(k => k.length > 2);
    
    const aMatches = keywords.filter(keyword => aTitle.includes(keyword)).length;
    const bMatches = keywords.filter(keyword => bTitle.includes(keyword)).length;
    
    return bMatches - aMatches;
  });
};

// Calculate profile completion
const calculateProfileCompletion = (profile) => {
  if (!profile) return 0;

  let profileCompletion = profile.profileCompletion || profile.profileStatus?.completionPercentage || 0;
  const isDraft = profile.isDraft === true;
  const showAsDraft = isDraft && profileCompletion < 20;

  if (profileCompletion === 0 && !showAsDraft) {
    const requiredFields = [
      'firstName', 'lastName', 'email', 'phone', 'dateOfBirth', 'gender',
      'nationality', 'currentCity', 'academicLevel', 'objective', 'preferredRole'
    ];
    
    const arrayFields = [
      'educationEntries', 'technicalSkills', 'softSkills', 'languages'
    ];
    
    const filledSimpleFields = requiredFields.filter(field => {
      const value = profile[field];
      return value && value.toString().trim() !== '';
    }).length;
    
    const filledArrayFields = arrayFields.filter(field => {
      const value = profile[field];
      return Array.isArray(value) && value.length > 0;
    }).length;
    
    const simplePercentage = (filledSimpleFields / requiredFields.length) * 60;
    const arrayPercentage = (filledArrayFields / arrayFields.length) * 40;
    profileCompletion = Math.round(simplePercentage + arrayPercentage);
  }

  return { profileCompletion, isDraft, showAsDraft };
};

// Normalize applications
const normalizeApplications = (applications) => {
  return (applications || []).map(app => {
    const interviewDate = app.interview_date || app.interviewDate || '';
    const interviewStart = app.interview_start_time || app.interviewStartTime || '';
    const interviewEnd = app.interview_end_time || app.interviewEndTime || '';
    const interviewMode = app.interview_mode || app.interviewMode || 'online';
    const interviewLink = app.interview_link || app.interviewLink || '';
    const interviewLocation = app.interview_location || app.interviewLocation || '';
    const interviewNotes = app.interview_notes || app.interviewNotes || '';
    const interviewType = app.interview_type || app.interviewType || 'internship';
    
    return {
      ...app,
      interview_date: interviewDate,
      interviewDate,
      interview_start_time: interviewStart,
      interviewStartTime: interviewStart,
      interview_end_time: interviewEnd,
      interviewEndTime: interviewEnd,
      interview_mode: interviewMode,
      interviewMode,
      interview_link: interviewLink,
      interviewLink,
      interview_location: interviewLocation,
      interviewLocation,
      interview_notes: interviewNotes,
      interviewNotes,
      interview_type: interviewType,
      interviewType
    };
  });
};

// Fetch intern profile
const fetchInternProfile = async (isAdminView, viewUserId) => {
  const headers = getAuthHeaders();
  const profileUrl = isAdminView && viewUserId 
    ? buildApiUrl(`/api/intern/profile?userId=${viewUserId}`)
    : buildApiUrl('/api/intern/profile');
  const profileRes = await axios.get(profileUrl, { headers }).catch(() => null);
  return profileRes?.data || {};
};

// Fetch applications
const fetchApplications = async (isAdminView, viewUserId) => {
  const headers = getAuthHeaders();
  const applicationsUrl = isAdminView && viewUserId 
    ? buildApiUrl(`/api/intern/applications?userId=${viewUserId}`)
    : buildApiUrl('/api/intern/applications');
  const applicationsRes = await axios.get(applicationsUrl, { headers }).catch(() => null);
  return applicationsRes?.data?.applications || [];
};

// Fetch internships
const fetchInternships = async () => {
  const headers = getAuthHeaders();
  const internshipsRes = await axios.get(buildApiUrl('/api/jobs/get_internships'), { headers }).catch(() => null);
  return internshipsRes?.data || [];
};

// Fetch saved internships
const fetchSavedInternships = async (isAdminView, viewUserId) => {
  const headers = getAuthHeaders();
  const savedUrl = isAdminView && viewUserId 
    ? buildApiUrl(`/api/intern/saved?userId=${viewUserId}`)
    : buildApiUrl('/api/intern/saved');
  const savedInternshipsRes = await axios.get(savedUrl, { headers }).catch(() => null);
  return savedInternshipsRes?.data?.saved || [];
};

// Fetch profile views
const fetchProfileViews = async (isAdminView, viewUserId) => {
  const headers = getAuthHeaders();
  const profileViewsUrl = isAdminView && viewUserId 
    ? buildApiUrl(`/api/intern/profile-views?userId=${viewUserId}`)
    : buildApiUrl('/api/intern/profile-views');
  const profileViewsRes = await axios.get(profileViewsUrl, { headers }).catch(() => null);
  return {
    total_views: profileViewsRes?.data?.total_views || 0,
    views_this_month: profileViewsRes?.data?.views_this_month || 0
  };
};

// Fetch AI recommendations
const fetchAIRecommendations = async (isAdminView, viewUserId) => {
  const headers = getAuthHeaders();
  try {
    const recommendationsUrl = isAdminView && viewUserId 
      ? buildApiUrl(`/api/intern/recommendations?userId=${viewUserId}`)
      : buildApiUrl('/api/intern/recommendations');
    const recommendationsRes = await axios.get(recommendationsUrl, { headers });
    return recommendationsRes?.data?.recommendations || [];
  } catch (error) {
    console.error('Error fetching AI recommendations:', error);
    return [];
  }
};

/**
 * Main hook for fetching all intern dashboard data
 * This combines all the individual queries and processes the data
 */
export const useInternDashboardData = (isAdminView = false, viewUserId = null) => {
  // Individual queries for each data source
  // Critical queries - load first (profile and applications for stats)
  const profileQuery = useQuery({
    queryKey: ['internProfile', isAdminView, viewUserId],
    queryFn: () => fetchInternProfile(isAdminView, viewUserId),
    staleTime: 5 * 60 * 1000, // 5 minutes - profile doesn't change often
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnMount: false, // Don't refetch if data exists in cache
    refetchOnWindowFocus: false
  });

  const applicationsQuery = useQuery({
    queryKey: ['internApplications', isAdminView, viewUserId],
    queryFn: () => fetchApplications(isAdminView, viewUserId),
    staleTime: 2 * 60 * 1000, // 2 minutes - applications can change
    cacheTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  // Non-critical queries - can load in parallel but don't block UI
  const internshipsQuery = useQuery({
    queryKey: ['internships'],
    queryFn: fetchInternships,
    staleTime: 3 * 60 * 1000, // 3 minutes - internships list changes moderately
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const savedInternshipsQuery = useQuery({
    queryKey: ['savedInternships', isAdminView, viewUserId],
    queryFn: () => fetchSavedInternships(isAdminView, viewUserId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    cacheTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const profileViewsQuery = useQuery({
    queryKey: ['internProfileViews', isAdminView, viewUserId],
    queryFn: () => fetchProfileViews(isAdminView, viewUserId),
    staleTime: 5 * 60 * 1000, // 5 minutes - views don't change frequently
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const recommendationsQuery = useQuery({
    queryKey: ['internRecommendations', isAdminView, viewUserId],
    queryFn: () => fetchAIRecommendations(isAdminView, viewUserId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    enabled: !!profileQuery.data // Only fetch if profile is loaded (needs preferredRole)
  });

  // Progressive loading states - check critical vs non-critical data
  // Critical data: profile, applications (needed for stats)
  // Non-critical: internships list, saved internships, profile views, recommendations
  const isCriticalDataLoading = profileQuery.isLoading || applicationsQuery.isLoading;
  const isNonCriticalDataLoading = internshipsQuery.isLoading || 
                                    savedInternshipsQuery.isLoading || 
                                    profileViewsQuery.isLoading ||
                                    (recommendationsQuery.isLoading && profileQuery.data);
  
  // Overall loading state (for initial load)
  const isLoading = isCriticalDataLoading && !profileQuery.data && !applicationsQuery.data;

  // Check if any query has error
  const isError = profileQuery.isError || 
                  applicationsQuery.isError || 
                  internshipsQuery.isError || 
                  savedInternshipsQuery.isError || 
                  profileViewsQuery.isError ||
                  recommendationsQuery.isError;

  // Memoize expensive calculations to prevent unnecessary recalculations
  const profile = useMemo(() => profileQuery.data || {}, [profileQuery.data]);
  const profileCompletionData = useMemo(() => calculateProfileCompletion(profile), [profile]);
  
  const normalizedApplications = useMemo(
    () => normalizeApplications(applicationsQuery.data || []),
    [applicationsQuery.data]
  );
  
  const appliedIds = useMemo(
    () => normalizedApplications.map(app => String(app.internshipId)),
    [normalizedApplications]
  );
  
  const transformedInternships = useMemo(
    () => transformInternships(internshipsQuery.data || []),
    [internshipsQuery.data]
  );
  
  const preferredRole = profile?.preferredRole;
  const sortedInternships = useMemo(
    () => sortByPreferredRole(transformedInternships, preferredRole),
    [transformedInternships, preferredRole]
  );
  
  const savedInternshipsTransformed = useMemo(
    () => transformSavedInternships(savedInternshipsQuery.data || []),
    [savedInternshipsQuery.data]
  );
  
  const profileViews = profileViewsQuery.data?.total_views || 0;
  const viewsThisMonth = profileViewsQuery.data?.views_this_month || 0;
  
  // Transform AI recommendations (memoized)
  const aiRecommendations = useMemo(() => {
    if (recommendationsQuery.data && recommendationsQuery.data.length > 0) {
      return recommendationsQuery.data.map(rec => ({
        _id: rec.id,
        jobTitle: rec.title,
        company: rec.company,
        location: rec.location,
        job_type: rec.type,
        salary_range: rec.stipend ? { min: 0, max: 0, display: rec.stipend } : null,
        description: rec.description,
        required_skills: rec.requiredSkills || [],
        posted_date: rec.postedDate,
        match_score: rec.matchScore,
        matchScore: rec.matchScore,
        companyLogo: rec.companyLogo,
        isAIRecommended: true
      }));
    } else if (recommendationsQuery.isError && sortedInternships.length > 0) {
      // Fallback: use top internships sorted by preferred role match
      return sortedInternships.slice(0, 10);
    }
    return [];
  }, [recommendationsQuery.data, recommendationsQuery.isError, sortedInternships]);

  const interviewCount = useMemo(() => {
    return normalizedApplications.filter(app => {
      const hasInterview = app.interviewDate || app.interview_date || 
                          (app.status || '').toLowerCase().includes('interview');
      return hasInterview;
    }).length;
  }, [normalizedApplications]);

  // Memoize interviews list to prevent recalculation
  const interviewsList = useMemo(() => {
    return normalizedApplications.filter(app => {
      const hasInterview = app.interviewDate || app.interview_date || 
                          (app.status || '').toLowerCase().includes('interview');
      return hasInterview;
    });
  }, [normalizedApplications]);

  // Combine all data into dashboard format (memoized for performance)
  // Provide default values to prevent undefined errors
  const dashboardData = useMemo(() => ({
    stats: {
      applications: normalizedApplications.length,
      interviews: interviewCount,
      profileViews: profileViews,
      savedInternships: savedInternshipsTransformed.length
    },
    profileCompletion: profileCompletionData.profileCompletion || 0,
    isDraft: profileCompletionData.isDraft || false,
    showAsDraft: profileCompletionData.showAsDraft || false,
    applications: normalizedApplications,
    interviews: interviewsList,
    internships: sortedInternships,
    recommendations: aiRecommendations,
    savedInternships: savedInternshipsTransformed,
    academicInfo: {
      currentYear: profile?.academicLevel || '3rd Year',
      gpa: profile?.educationEntries?.[0]?.gpa || '3.8/4.0',
      graduation: profile?.educationEntries?.[0]?.endDate || 'May 2025',
      university: profile?.educationEntries?.[0]?.institution || 'University',
      major: profile?.educationEntries?.[0]?.fieldOfStudy || 'Computer Science'
    },
    portfolio: [],
    projects: profile?.projectEntries || [],
    profileViewsThisMonth: viewsThisMonth
  }), [
    normalizedApplications,
    interviewCount,
    profileViews,
    savedInternshipsTransformed.length,
    profileCompletionData,
    interviewsList,
    sortedInternships,
    aiRecommendations,
    profile,
    viewsThisMonth
  ]);

  // Refetch function (for manual refresh)
  // Returns a promise that resolves when all queries are refetched
  const refetch = async () => {
    await Promise.all([
      profileQuery.refetch(),
      applicationsQuery.refetch(),
      internshipsQuery.refetch(),
      savedInternshipsQuery.refetch(),
      profileViewsQuery.refetch(),
      profileQuery.data ? recommendationsQuery.refetch() : Promise.resolve()
    ]);
  };

  return {
    dashboardData,
    appliedInternshipIds: appliedIds,
    isLoading,
    isCriticalDataLoading,
    isNonCriticalDataLoading,
    isError,
    refetch,
    // Expose individual query states if needed
    profileQuery,
    applicationsQuery,
    internshipsQuery,
    savedInternshipsQuery,
    profileViewsQuery,
    recommendationsQuery
  };
};

